import { useEffect } from "react";
import {
  atom,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { getEvents, stopEvents } from "./MessagingApi";
import { Me } from "./MeApi";

export const Hand = atom({
    key: "NertzHand",
    default: [],
  }),
  Game = atom({
    key: "NertzGame",
    default: {},
  }),
  Deck = atom({
    key: "NertzDeck",
    default: [],
  }),
  MePaused = selector({
    key: "NertzMePaused",
    get: ({ get }) => {
      const player = get(MePlayer);

      return player ? player.isPaused : false;
    },
  }),
  MePlayer = selector({
    key: "NertzMePlayer",
    get: ({ get }) => {
      const me = get(Me),
        game = get(Game);

      return game && game.players && me
        ? game.players.find((p) => p.id === me.userId)
        : null;
    },
  }),
  MeReady = selector({
    key: "NertzMeReady",
    get: ({ get }) => {
      const player = get(MePlayer);

      return player ? player.isReady : false;
    },
  }),
  MeStuck = selector({
    key: "NertzMeStuck",
    get: ({ get }) => {
      const player = get(MePlayer);

      return player ? player.isStuck : false;
    },
  }),
  AllReady = selector({
    key: "NertzAllReady",
    get: ({ get }) => {
      const game = get(Game);

      return game && game.players
        ? !game.players.some((p) => !p.isReady)
        : false;
    },
  }),
  AllStuck = selector({
    key: "NertzAllStuck",
    get: ({ get }) => {
      const game = get(Game);

      return game && game.players
        ? !game.players.some((p) => !p.isStuck)
        : false;
    },
  }),
  OtherPaused = selector({
    key: "NertzOtherPaused",
    get: ({ get }) => {
      const game = get(Game),
        mePaused = get(MePaused);

      return !mePaused && game && game.players
        ? game.players.some((p) => p.isPaused)
        : false;
    },
  }),
  NertzCaller = atom({
    key: "NertzCaller",
    default: null,
  }),
  NertzQuitter = atom({
    key: "NertzQuitter",
    default: null,
  }),
  NertzWinner = atom({
    key: "NertzWinner",
    default: null,
  }),
  NertzGames = atom({
    key: "NertzGames",
    default: [],
  }),
  Pile = atom({
    key: "NertzPile",
    default: [],
  }),
  River = atom({
    key: "NertzRiver",
    default: [],
  }),
  Stuck = atom({
    key: "NertzStuck",
    default: false,
  }),
  StuckCount = atom({
    key: "NertzStuckCount",
    default: 0,
  }),
  Work = atom({
    key: "NertzWork",
    default: [[], [], [], []],
  });

export function NertzGameApi({ id }) {
  const [game, setGame] = useRecoilState(Game),
    allStuck = useRecoilValue(AllStuck),
    [stuckCount, setStuckCount] = useRecoilState(StuckCount),
    setStuck = useSetRecoilState(Stuck),
    setPile = useSetRecoilState(Pile),
    setDeck = useSetRecoilState(Deck),
    setRiver = useSetRecoilState(River),
    setCaller = useSetRecoilState(NertzCaller),
    setQuitter = useSetRecoilState(NertzQuitter),
    setWinner = useSetRecoilState(NertzWinner),
    me = useRecoilValue(Me),
    meReady = useRecoilValue(MeReady);

  useEffect(() => {
    getGame(id).then((result) => {
      if (!result.isLocked && result.ownerId === me?.userId) {
        lockNertzGame(result.id);
        result.isLocked = true;
      }

      setGame(result);
    });
  }, [id, setGame, me]);

  useEffect(() => {
    const handlers = {
      playerMessage: (payload) => {
        if (payload.nertz) {
          const players = game.players
            .map((p) =>
              p.id === payload.id
                ? {
                    ...p,
                    ...{
                      score: p.score + 10,
                      scores: [...(p.scores || []), p.score + 10],
                    },
                  }
                : { ...p, scores: [...(p.scores || []), p.score] }
            )
            .sort((a, b) => b.score - a.score);

          if (
            players[0].score >= game.playTo &&
            players[0].score > players[1]?.score
          ) {
            if (me.userId === game.ownerId) {
              nertzGameWinner(game.id, players[0].id);
            }
            setWinner(players[0].id);
          } else {
            setCaller(payload.id);
          }

          setGame({ ...game, ...{ players: players } });
        }

        if (payload.river) {
          setRiver((river) =>
            river.map((r, i) =>
              i === payload.river.index ? { ...payload.river } : r
            )
          );
        }

        if (payload.player) {
          if (
            payload.player.isReady &&
            meReady &&
            game.players.some((p) => !p.isReady)
          ) {
            nertzPlayerMessage(id, { player: { isReady: true } });
          }

          setGame({
            ...game,
            ...{
              players: game.players.map((p) =>
                p.id === payload.id
                  ? {
                      ...p,
                      ...payload.player,
                      ...(payload.player.isReady && !p.isReady
                        ? { nertz: 13, score: p.score - 26 }
                        : {}),
                    }
                  : p
              ),
            },
          });
        }

        if (payload.score) {
          const unStick =
            payload.score.score === 1 || payload.score.score === 3;

          setGame({
            ...game,
            ...{
              players: game.players.map((p) =>
                p.id === payload.id
                  ? {
                      ...p,
                      ...{
                        score: p.score + (payload.score.score || 0),
                        nertz: p.nertz - (payload.score.nertz || 0),
                      },
                      ...(unStick ? { isStuck: false } : {}),
                    }
                  : { ...p, ...(unStick ? { isStuck: false } : {}) }
              ),
            },
          });

          if (unStick) {
            setStuck(false);
          }
        }

        if (payload.quit) {
          setQuitter(payload.id);
        }
      },
    };

    getEvents(id, handlers);

    return () => {
      stopEvents(id);
    };
  }, [
    id,
    game,
    setGame,
    setRiver,
    setStuck,
    meReady,
    setCaller,
    setQuitter,
    setWinner,
    me,
  ]);

  useEffect(() => {
    if (allStuck) {
      if (stuckCount === 3) {
        setPile((pile) => [...pile.slice(1), pile[0]]);
        setStuckCount(0);
      } else {
        setDeck((deck) => [...deck.slice(1), deck[0]]);
        setStuckCount((count) => count + 1);
      }

      setGame({
        ...game,
        ...{
          players: game.players.map((p) => ({
            ...p,
            ...{
              isStuck: false,
            },
          })),
        },
      });

      setStuck(false);
    }
  }, [
    allStuck,
    stuckCount,
    setPile,
    setStuckCount,
    setDeck,
    setGame,
    game,
    setStuck,
  ]);

  return <></>;
}

export function NertzGamesApi() {
  const [games, setGames] = useRecoilState(NertzGames);

  useEffect(() => {
    getGames().then((result) => {
      setGames(result);
    });
  }, [setGames]);

  useEffect(() => {
    const handlers = {
      gameCreated: (payload) => {
        setGames([...games, payload]);
      },
      gameDeleted: (payload) => {
        setGames(games.filter((g) => g.id !== payload));
      },
      gameUpdated: (payload) => {
        setGames(games.map((g) => (g.id === payload.id ? payload : g)));
      },
    };

    getEvents("nertz", handlers);

    return () => {
      stopEvents("nertz");
    };
  }, [games, setGames]);

  return <></>;
}

export async function createNertzGame(model) {
  const response = await fetch("/api/nertz/game", {
    method: "POST",
    body: JSON.stringify(model),
  });

  if (response.ok) {
    return;
  }
}

export async function deleteNertzGame(id) {
  const response = await fetch(`/api/nertz/game/${id}`, { method: "DELETE" });

  if (response.ok) {
    return;
  }
}

async function lockNertzGame(id) {
  const response = await fetch(`/api/nertz/game/${id}/lock`);

  if (response.ok) {
    return;
  }
}

export async function joinNertzGame(id) {
  const response = await fetch(`/api/nertz/game/${id}/join`);

  if (response.ok) {
    return;
  }
}

async function nertzGameWinner(id, winnerId) {
  const response = await fetch(`/api/nertz/game/${id}/win/${winnerId}`);

  if (response.ok) {
    return;
  }
}

export async function nertzPlayerMessage(id, payload) {
  const response = await fetch(`/api/nertz/game/${id}/message`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    return;
  }
}

async function getGame(id) {
  const response = await fetch(`/api/nertz/game/${id}`);

  if (response.ok) {
    return await response.json();
  }
}

async function getGames() {
  const response = await fetch("/api/nertz/game");

  if (response.ok) {
    return await response.json();
  }
}
